import { ExpressionReturnType } from '../expressions/common';
import { Expression } from './expression';

export interface ActivityDefinitionPdfTemplate {
  attachment_id: string;
  activity_definition_id: string;
  title: string;
  instructions: string;
  labels: string[];
  mapping: PdfFormMapping;
  start_date: string;
  end_date: string;
  start_page: number;
  end_page: number;
  sort: number;
  visibility: Expression | null;
  property_id: number | null;
  referenced_properties: number[];
  update_user_id: string;
  update_organization_id: string;
  attachment_update_datetime: string;
}

export type PdfFieldType = 'text' | 'checkbox' | 'radiobutton' | 'combobox';

export const supportedPdfFieldTypes: PdfFieldType[] = [
  'text',
  'checkbox',
  'radiobutton',
  'combobox'
];

export function isSupportedPdfFieldType(type: unknown): type is PdfFieldType {
  return supportedPdfFieldTypes.includes(type as PdfFieldType);
}

export interface PdfFieldMapping {
  name: string;
  type: PdfFieldType;
  id: string;
  page: number;
  editable?: boolean;
  hidden?: boolean;
  multiline?: boolean;
  charlimit?: number;
  export_values?: string;
  default_value?: string;
  value: string;
  expression: Expression | null;
}

export interface PdfFormMapping {
  readonly: boolean;
  fields: PdfFieldMapping[];
  signatures: PdfSignatureConfig;
  include_attachments: { property_id: number }[];
}

export const pdfFieldTypeToExpressionTypes: Record<
  PdfFieldType,
  ExpressionReturnType[]
> = {
  text: ['string', 'int', 'decimal', 'date', 'datetime'],
  checkbox: ['boolean'],
  radiobutton: ['boolean'],
  combobox: ['string']
};

export interface PdfSigner {
  /** The identifier name- eg $EMPLOYER */
  identifier: string;
  /** The label to show in the UI */
  label: string;
  /** The expression to evaluate for the signer's name */
  full_name_expression: Expression;
  /** The expression to evaluate for the signer's email */
  email_expression: Expression;
  /** The expression to evaluate for including the signer */
  include_expression: Expression | null;
}

export enum PdfSigningMethod {
  Basic = 0,
  WetInk = 1,
  KBA = 2
}

export interface PdfSignatureConfig {
  signing_method: PdfSigningMethod;
  signers: PdfSigner[];
}

export const maxPdfSigners = 4;
export const maxPdfSignerFields = 10;
export const maxPdfIncludeAttachments = 4;

export const US_LETTER_WIDTH = 612;
export const US_LETTER_HEIGHT = 792;
const PDF_SIZE_TOLERANCE = 0.5;

export function isUSLetterPage(width: number, height: number) {
  return (
    Math.abs(width - US_LETTER_WIDTH) < PDF_SIZE_TOLERANCE &&
    Math.abs(height - US_LETTER_HEIGHT) < PDF_SIZE_TOLERANCE
  );
}

export function isUnusualPageSizePrecision(width: number, height: number) {
  return width.toString().length > 7 || height.toString().length > 7;
}

export type PdfViewerFieldPMapping = Pick<
  PdfFieldMapping,
  'id' | 'name' | 'export_values' | 'type'
> & { value: string | null };

export type PrepareDocumentsItemInfo =
  | {
      type: 'pdf';
      title: string;
      filename: string;
      start_page: number;
      end_page: number;
      readonly: boolean;
    }
  | {
      type: 'image';
      title: string;
      filename: string;
      content_type: string;
      group: string;
      cors_url: string;
    };
